import axios from 'axios';
import { env } from '../config/env';
import { handleAuthExpiry } from '../lib/authRedirect';
import { extractApiErrorMessage } from '../utils/generation';

const api = axios.create({
  baseURL: env.INFERENCE_API_URL,
  headers: { 'Content-Type': 'application/json' },
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('inference_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      handleAuthExpiry('inference_token', '/inference');
    }
    return Promise.reject(error);
  }
);

export interface CatalogModel {
  id: string;
  display_name: string;
  description?: string;
  task_type?: string;
  context_length: number;
  parameters_b?: number;
}

export interface PriceRow {
  id: string;
  display_name: string;
  context_length: number;
  input_price_per_1m_inr: number;
  output_price_per_1m_inr: number;
  pricing_available: boolean;
}

// One slab of a tiered ("slab") price list - e.g. a per-resolution price for
// video generation. Mirrors the backend's catalogPriceTier JSON shape
// (services/inference-service/internal/handlers/models.go).
export interface PriceTier {
  key: string;
  unit_price: number;
  default: boolean;
}

export interface CatalogEntry {
  id: string;
  display_name: string;
  description: string;
  category: string;
  provider: string;
  provider_logo_url: string;
  tags: string[];
  context_length: number;
  parameters_b: number;
  created_at: string;
  currency: string;
  billing_unit: string;
  input_price_per_1m: number;
  output_price_per_1m: number;
  pricing_available: boolean;
  // [] when untiered. When non-empty, input_price_per_1m already equals the
  // default tier's resolved price (guaranteed by the catalog API) - the
  // tiers are additional per-slab prices, not an alternative to it.
  price_tiers: PriceTier[];
  // True for fal-sourced models with a non-empty stored input schema (see
  // GET /v1/generate/:model_id/schema). Gates whether the dynamic
  // schema-driven form can be offered for this model.
  has_input_schema: boolean;
}

const normalizePriceTier = (raw: any): PriceTier => ({
  key: raw?.key ?? '',
  unit_price: Number(raw?.unit_price) || 0,
  default: Boolean(raw?.default),
});

const normalizeCatalogEntry = (raw: any): CatalogEntry => ({
  id: raw?.id ?? '',
  display_name: raw?.display_name ?? '',
  description: raw?.description ?? '',
  category: raw?.category ?? '',
  provider: raw?.provider ?? '',
  provider_logo_url: raw?.provider_logo_url ?? '',
  tags: Array.isArray(raw?.tags) ? raw.tags : [],
  context_length: Number(raw?.context_length) || 0,
  parameters_b: Number(raw?.parameters_b) || 0,
  created_at: raw?.created_at ?? '',
  currency: raw?.currency ?? '',
  billing_unit: raw?.billing_unit ?? 'per_1m_tokens',
  input_price_per_1m: Number(raw?.input_price_per_1m) || 0,
  output_price_per_1m: Number(raw?.output_price_per_1m) || 0,
  pricing_available: Boolean(raw?.pricing_available),
  price_tiers: Array.isArray(raw?.price_tiers) ? raw.price_tiers.map(normalizePriceTier) : [],
  has_input_schema: raw?.has_input_schema ?? false,
});

// /v1/pricing omits the *_inr fields for zero-priced (free) models, so fall
// back to the generic per-1m price (the API is INR-denominated), then 0.
export const normalizePriceRow = (
  m: Partial<PriceRow> & { input_price_per_1m?: number; output_price_per_1m?: number }
): PriceRow => ({
  id: m.id ?? '',
  display_name: m.display_name ?? '',
  context_length: m.context_length ?? 0,
  input_price_per_1m_inr: m.input_price_per_1m_inr ?? m.input_price_per_1m ?? 0,
  output_price_per_1m_inr: m.output_price_per_1m_inr ?? m.output_price_per_1m ?? 0,
  pricing_available: m.pricing_available ?? false,
});

export interface InferenceApiKey {
  id: string;
  name: string;
  key_prefix: string;
  is_active: boolean;
  requests_per_min: number;
  tokens_per_min: number;
  max_concurrency: number;
  monthly_token_limit: number | null;
  store_data: boolean;
  cache_enabled: boolean;
  last_used_at?: string;
  created_at: string;
}

export interface UsageSummary {
  total_requests: number;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_cost: number;
  avg_latency_ms?: number;
  avg_ttft_ms?: number;
  cached_requests: number;
}

export interface WalletBalance {
  balance: number;
  available_balance: number;
  account_type: string;
  currency: string;
}

export interface UsageRequest {
  request_id: string;
  model_name: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  total_cost: number;
  status: string;
  cache_hit: boolean;
  total_latency_ms?: number;
  created_at: string;
}

export interface TokenUsage {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
}

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface KeyLimitsUpdate {
  requests_per_min?: number;
  tokens_per_min?: number;
  max_concurrency?: number;
  monthly_token_limit?: number;
  store_data?: boolean;
  cache_enabled?: boolean;
}

export const inferenceService = {
  listModels: () => api.get<{ data?: CatalogModel[]; models?: CatalogModel[] } | CatalogModel[]>('/v1/models'),
  // Public - used by the logged-out landing page too.
  getPublicPricing: () =>
    axios
      .get<{ models: PriceRow[] }>(`${env.INFERENCE_API_URL.replace(/\/$/, '')}/v1/pricing`)
      .then((r) => ({
        ...r,
        data: { ...r.data, models: (r.data.models ?? []).map(normalizePriceRow) },
      })),
  getCatalog: async (): Promise<{ models: CatalogEntry[]; currency: string }> => {
    const res = await axios.get<{ models: any[]; currency?: string }>(
      `${env.INFERENCE_API_URL.replace(/\/$/, '')}/v1/catalog`
    );
    const models = Array.isArray(res.data?.models) ? res.data.models.map(normalizeCatalogEntry) : [];
    return { models, currency: res.data?.currency ?? 'INR' };
  },
  listApiKeys: () => api.get<{ data: InferenceApiKey[] }>('/v1/security/api-keys'),
  updateApiKeyLimits: (id: string, limits: KeyLimitsUpdate) =>
    api.patch(`/v1/security/api-keys/${id}/limits`, limits),
  getUsageSummary: (from?: string, to?: string) =>
    api.get<{ summary: UsageSummary }>('/v1/usage/summary', { params: { from, to } }),
  getWalletBalance: () => api.get<WalletBalance>('/v1/wallet/balance'),
  getUsageRequests: (params: { limit?: number; offset?: number; cached?: boolean }) =>
    api.get<{ data: UsageRequest[]; total?: number; limit?: number; offset?: number }>('/v1/usage/requests', {
      params,
    }),
};

// streamChat POSTs a streaming chat completion and invokes onDelta per
// chunk. Reasoning models (e.g. GLM) stream thinking tokens in
// delta.reasoning / delta.reasoning_content with content empty, so each
// delta carries its kind. Uses fetch (axios cannot consume SSE bodies).
export async function streamChat(
  model: string,
  messages: ChatMessage[],
  params: { temperature?: number; max_tokens?: number },
  onDelta: (text: string, kind: 'content' | 'reasoning') => void,
  signal?: AbortSignal,
  apiKeyOverride?: string | null,
  onUsage?: (usage: TokenUsage) => void
): Promise<{ finishReason: string | null }> {
  const token = apiKeyOverride || localStorage.getItem('inference_token');
  const resp = await fetch(`${env.INFERENCE_API_URL.replace(/\/$/, '')}/v1/chat/completions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ model, messages, stream: true, stream_options: { include_usage: true }, ...params }),
    signal,
  });
  if (!resp.ok || !resp.body) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err?.error?.message || `Request failed (${resp.status})`);
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  // "length" here means the response was cut off by max_tokens.
  let finishReason: string | null = null;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const data = line.slice(6).trim();
      if (data === '[DONE]') return { finishReason };
      try {
        const chunk = JSON.parse(data);
        const choice = chunk.choices?.[0];
        const delta = choice?.delta;
        if (delta?.content) onDelta(delta.content, 'content');
        const reasoning = delta?.reasoning ?? delta?.reasoning_content;
        if (reasoning) onDelta(reasoning, 'reasoning');
        if (choice?.finish_reason) finishReason = choice.finish_reason;
        if (chunk.usage && onUsage) onUsage(chunk.usage);
      } catch {
        /* partial line - ignored */
      }
    }
  }
  return { finishReason };
}

// --- Generation (image/video/speech/transcription/audio) ---------------
//
// Async job flow: submitGeneration kicks off a job (202 Accepted),
// getGenerationStatus polls it, and getGenerationResult fetches the final
// body once ready (callers apply isNotReadyResult from utils/generation to
// detect a not-ready poll response). fetchGenerationContent downloads the
// binary payload referenced by a result's content_url.

export interface GenerationJob {
  id: string;
  status: string;
  queue_position?: number;
}

async function parseJsonError(resp: Response): Promise<never> {
  const json = await resp.json().catch(() => ({}));
  throw new Error(extractApiErrorMessage(json));
}

export async function submitGeneration(
  modelId: string,
  body: unknown,
  apiKeyOverride?: string | null
): Promise<GenerationJob> {
  const token = apiKeyOverride || localStorage.getItem('inference_token');
  const resp = await fetch(
    `${env.INFERENCE_API_URL.replace(/\/$/, '')}/v1/generate/${encodeURIComponent(modelId)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    }
  );
  if (!resp.ok) return parseJsonError(resp);
  return resp.json();
}

export async function getGenerationStatus(
  modelId: string,
  generationId: string,
  apiKeyOverride?: string | null
): Promise<GenerationJob> {
  const token = apiKeyOverride || localStorage.getItem('inference_token');
  const resp = await fetch(
    `${env.INFERENCE_API_URL.replace(/\/$/, '')}/v1/generate/${encodeURIComponent(modelId)}/requests/${generationId}/status`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!resp.ok) return parseJsonError(resp);
  return resp.json();
}

export async function getGenerationResult(
  modelId: string,
  generationId: string,
  apiKeyOverride?: string | null
): Promise<unknown> {
  const token = apiKeyOverride || localStorage.getItem('inference_token');
  const resp = await fetch(
    `${env.INFERENCE_API_URL.replace(/\/$/, '')}/v1/generate/${encodeURIComponent(modelId)}/requests/${generationId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!resp.ok) return parseJsonError(resp);
  return resp.json();
}

// Fetches the fal input schema for a model (GET .../generate/:model_id/schema).
// 404 ("no input schema for this model") resolves to null rather than
// throwing - the caller falls back to the generic prompt-only form. Any
// other non-2xx still throws via parseJsonError, same as the other
// generate/* calls.
export async function getGenerationSchema(
  modelId: string,
  apiKeyOverride?: string | null
): Promise<unknown | null> {
  const token = apiKeyOverride || localStorage.getItem('inference_token');
  const resp = await fetch(
    `${env.INFERENCE_API_URL.replace(/\/$/, '')}/v1/generate/${encodeURIComponent(modelId)}/schema`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (resp.status === 404) return null;
  if (!resp.ok) return parseJsonError(resp);
  const json = await resp.json();
  return json?.schema ?? null;
}

export async function fetchGenerationContent(contentUrl: string, apiKeyOverride?: string | null): Promise<Blob> {
  const token = apiKeyOverride || localStorage.getItem('inference_token');
  const resp = await fetch(`${env.INFERENCE_API_URL.replace(/\/$/, '')}${contentUrl}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) return parseJsonError(resp);
  return resp.blob();
}
